<template>
    <div class="fixed bottom-6 right-6 z-50 font-sans">
        <button 
            type="button" 
            class="group relative w-16 h-16 rounded-full shadow-[0_8px_30px_rgb(0,0,0,0.2)] bg-slate-800 text-white flex items-center justify-center hover:bg-slate-900 transition-all duration-300 active:scale-90"
            @click="toggleOpen()"
        >
            <div v-if="!open" class="absolute -top-0.5 -right-0.5 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white animate-pulse"></div>
            <i :class="open ? 'fas fa-times text-2xl' : 'fas fa-comment-dots text-2xl group-hover:rotate-12 transition-transform'"></i>
        </button>

        <transition name="panel">
            <div v-if="open" class="absolute bottom-20 right-0 w-[400px] max-w-[calc(100vw-2rem)] h-[600px] max-h-[calc(100vh-8rem)] bg-white rounded-[2rem] shadow-[0_20px_60px_rgba(0,0,0,0.2)] border border-slate-100 overflow-hidden flex flex-col">
                
                <div class="px-6 py-5 bg-slate-800 text-white flex items-center justify-between shadow-lg">
                    <div class="flex items-center gap-4">
                        <!-- <div class="relative">
                            <img src="https://r2-us-west.photoai.com/1726207008-41ab0a06a91e13c70416df3d1712030b-4.png" class="w-12 h-12 rounded-2xl object-cover" />
                        </div> -->
                        <div>
                            <!-- <div class="font-bold text-lg tracking-tight leading-none mb-1">Termoflow</div> -->
                            <div class="flex items-center gap-3 text-white">
                                <span class="flex h-2.5 w-2.5 relative">
                                    <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                    <span class="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                                </span>
                                Conseiller virtuel
                            </div>
                        </div>
                    </div>
                    <button @click="toggleOpen" class="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                        <i class="fas fa-chevron-down text-sm"></i>
                    </button>
                </div>

                <div ref="messages" class="flex-1 overflow-y-auto px-5 py-6 space-y-4 bg-slate-50/50 scroll-smooth">
                    <transition-group name="msg">
                        <div v-for="(msg, idx) in messages" :key="idx" 
                             class="flex animate-in fade-in slide-in-from-bottom-2 duration-300" 
                             :class="msg.role === 'user' ? 'justify-end' : 'justify-start'">
                            
                            <div class="max-w-[85%]">
                                <div :class="[
                                    'px-4 py-3 shadow-sm text-sm leading-relaxed transition-all',
                                    msg.role === 'user' 
                                        ? 'bg-slate-800 text-white rounded-[1.3rem] rounded-tr-none' 
                                        : 'bg-white text-slate-800 border border-slate-200 rounded-[1.3rem] rounded-tl-none'
                                ]">
                                    <div v-html="msg.text" class="prose prose-sm prose-slate"></div>
                                </div>
                                <div class="mt-1.5 text-[10px] text-slate-400 font-semibold px-1 uppercase tracking-tighter" :class="msg.role === 'user' ? 'text-right' : 'text-left'">
                                    {{ formatTime(msg.created_at) }}
                                </div>
                            </div>
                        </div>
                    </transition-group>

                    <div v-if="isTyping" class="flex justify-start items-center gap-2">
                        <div class="bg-white border border-slate-200 rounded-[1.3rem] rounded-tl-none px-4 py-3 shadow-sm flex gap-1">
                            <span class="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style="animation-delay: 0s"></span>
                            <span class="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style="animation-delay: 0.2s"></span>
                            <span class="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style="animation-delay: 0.4s"></span>
                        </div>
                    </div>
                </div>

                <div class="p-4 bg-white border-t border-slate-100 shadow-[0_-10px_40px_rgba(0,0,0,0.02)]">
                    <div class="relative flex items-center gap-2 bg-slate-100 rounded-2xl p-1.5 focus-within:bg-white focus-within:ring-2 focus-within:ring-slate-800/5 transition-all duration-300">
                        
                        <textarea 
                            ref="chatInput"
                            v-model="draft" 
                            rows="1"
                            @input="adjustTextarea"
                            class="flex-1 bg-transparent border-none focus:outline-none text-sm py-2.5 px-3 max-h-32 resize-none overflow-y-auto text-slate-700 leading-relaxed"
                            :placeholder="isTyping ? 'Réfléxion...' : 'Posez votre question...'"
                            @keydown.enter="onEnter"
                        ></textarea>
                        
                        <button 
                            @click="send" 
                            :disabled="!canSend || isTyping"
                            class="w-10 h-10 rounded-xl bg-slate-800 text-white flex items-center justify-center disabled:opacity-20 transition-all hover:bg-slate-900 active:scale-95 shadow-md shrink-0 mb-0.5"
                        >
                            <i class="fas fa-paper-plane text-xs"></i>
                        </button>
                    </div>
                    
                    <div class="mt-2.5 flex items-center justify-between px-1">
                        <!-- <span class="text-[9px] text-slate-400 font-bold uppercase tracking-widest">Propulsé par Termoflow</span> -->
                        <span v-if="error" class="text-[9px] text-red-500 font-bold">{{ error }}</span>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
export default {
    name: 'AssistanceChatbot',
    data() {
        return {
            open: false,
            draft: '',
            isTyping: false,
            error: null,
            chatKey: null,
            messages: []
        };
    },
    computed: {
        canSend() { return this.draft && this.draft.trim().length > 0; }
    },
    methods: {
        // --- LOGIQUE DE FOCUS ---
        focusInput() {
            this.$nextTick(() => {
                if (this.$refs.chatInput) {
                    this.$refs.chatInput.focus();
                }
            });
        },

        toggleOpen() {
            this.open = !this.open;
            if (this.open) {
                this.scrollToBottom();
                this.focusInput();
            }
        },

        adjustTextarea(e) {
            const el = e ? e.target : this.$refs.chatInput;
            if (!el) return;
            el.style.height = 'auto';
            el.style.height = (el.scrollHeight) + 'px';
        },

        onEnter(e) {
            if (!e.shiftKey) {
                e.preventDefault();
                this.send();
            }
        },

        async send() {
            if (!this.canSend || this.isTyping) return;

            const text = this.draft.trim();
            this.draft = '';
            this.error = null;
            
            // Reset hauteur textarea
            this.$nextTick(() => {
                if (this.$refs.chatInput) this.$refs.chatInput.style.height = 'auto';
            });

            this.messages.push({ role: 'user', text, created_at: new Date() });
            this.scrollToBottom();
            this.focusInput(); // Focus après envoi

            this.isTyping = true;
            
            try {
                const res = await this.$request.post('/assistance/ask', { 
                    message: text,
                    chat_key: this.chatKey 
                });

                const answer = res && res.answer ? String(res.answer) : null;
                
                this.messages.push({ 
                    role: 'assistant', 
                    text: answer || "Désolé, je n'ai pas pu générer de réponse.", 
                    created_at: new Date() 
                });
            } catch (e) {
                this.error = "Erreur de connexion";
                this.messages.push({ 
                    role: 'assistant', 
                    text: "Oups ! J'ai un petit problème technique. Réessayez dans un instant.", 
                    created_at: new Date() 
                });
            } finally {
                this.isTyping = false;
                this.scrollToBottom();
                this.focusInput(); // Focus après réponse
            }
        },

        scrollToBottom() {
            this.$nextTick(() => {
                const el = this.$refs.messages;
                if (el) el.scrollTop = el.scrollHeight;
            });
        },

        formatTime(date) {
            return new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' }).format(date);
        },

        generateKey() {
            return 'chat_' + crypto.randomUUID() + '_' + Date.now();
        }
    },
    created() {
        this.chatKey = this.generateKey();
    },
    mounted() {
        // Message de bienvenue initial
        const prenom = this.$account?.account?.prenom || '';
        this.messages.push({
            role: 'assistant',
            text: `Bonjour ${prenom} 👋<br />Je suis votre assistant personnalisé. Comment puis-je vous aider aujourd'hui ?`,
            created_at: new Date()
        });
    }
};
</script>

<style scoped>
/* Transition du Panel */
.panel-enter-active, .panel-leave-active {
    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}
.panel-enter-from, .panel-leave-to {
    opacity: 0;
    transform: translateY(30px) scale(0.9);
}

/* Transition des Bulles */
.msg-enter-active {
    transition: all 0.3s ease-out;
}
.msg-enter-from {
    opacity: 0;
    transform: translateY(10px);
}

/* Custom Scrollbar */
::-webkit-scrollbar {
    width: 4px;
}
::-webkit-scrollbar-track {
    background: transparent;
}
::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 10px;
}
::-webkit-scrollbar-thumb:hover {
    background: #cbd5e1;
}

/* Markdown Style Overrides */
.prose {
    max-width: none;
}
.prose p {
    margin: 0;
}
</style>